// src/config/appConfig.js
class AppConfig {
  constructor() {
    this.isDevelopment = import.meta.env.MODE === 'development';
    this.isProduction = import.meta.env.MODE === 'production';
    
    // Base URL - use window.location.origin in production, localhost in development
    this.baseUrl = this.isDevelopment 
      ? 'http://localhost:3000'
      : (typeof window !== 'undefined' ? window.location.origin : 'https://platform.quailgame.com');
    
    // API Base URL - from environment variables (GitHub secrets in production)
    this.apiBaseUrl = import.meta.env.REACT_APP_API_URL || import.meta.env.REACT_APP_API_BASE_URL;
    
    // Auth0 Configuration - from environment variables (GitHub secrets in production)
    this.auth0Domain = import.meta.env.REACT_APP_AUTH0_DOMAIN;
    this.auth0ClientId = import.meta.env.REACT_APP_AUTH0_CLIENT_ID;
    this.auth0Audience = import.meta.env.REACT_APP_AUTH0_AUDIENCE;

    // Registration
    this.registrationSenderEmail = 'info@quailgame.ca';
  }
  
  // Dynamic Auth0 redirect URI (getter method called at runtime)
  get auth0RedirectUri() {
    if (this.isDevelopment) {
      return import.meta.env.REACT_APP_AUTH0_REDIRECT_URI || 'http://localhost:3000/callback';
    }
    // Production: use current origin for Auth0 callbacks
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/callback`;
    }
    // Fallback for build time
    return 'https://platform.quailgame.com/callback';
  }
  
  // Dynamic Auth0 logout URI (getter method called at runtime)
  get auth0LogoutUri() {
    if (this.isDevelopment) {
      return import.meta.env.REACT_APP_AUTH0_LOGOUT_URI || 'http://localhost:3000/';
    }
    // Production: use current origin for logout
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/`;
    }
    // Fallback for build time
    return 'https://platform.quailgame.com/';
  }
  
  // Helper method to get the current subdomain
  getSubdomain() {
    if (this.isDevelopment) return 'localhost';
    
    // Handle build time when window doesn't exist
    if (typeof window === 'undefined') return 'platform';
    
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    
    // For subdomains like basoccer.quailgame.com or platform.quailgame.com
    if (parts.length >= 3) {
      return parts[0]; // Returns 'basoccer' or 'platform'
    }
    
    return 'www'; // Default fallback
  }
  
  // Helper method to determine app context based on subdomain
  getAppContext() {
    const subdomain = this.getSubdomain();
    
    switch (subdomain) {
      case 'basoccer':
      case 'soccer':
        return 'soccer';
      case 'platform':
      case 'admin':
        return 'platform';
      case 'localhost':
        return 'development';
      default:
        return 'platform'; // Default to platform
    }
  }
  
  // Get app title based on context
  getAppTitle() {
    const context = this.getAppContext();
    
    switch (context) {
      case 'soccer':
        return 'BA Soccer Manager';
      case 'platform':
        return 'Quail Game Platform';
      case 'development':
        return 'Quail Game (Dev)';
      default:
        return 'Quail Game';
    }
  }
  
  // Get app theme/branding based on context
  getAppTheme() {
    const context = this.getAppContext();
    
    switch (context) {
      case 'soccer':
        return 'soccer-theme';
      case 'platform':
        return 'platform-theme';
      default:
        return 'default-theme';
    }
  }
  
  // Validation method
  validate() {
    const required = [
      'apiBaseUrl',
      'auth0Domain', 
      'auth0ClientId',
      'auth0Audience'
    ];
    
    const missing = required.filter(key => !this[key]);
    
    if (missing.length > 0) {
      console.error('Missing required configuration:', missing);
      return false;
    }
    
    return true;
  }
  
  // Debug method to log current config (without sensitive data)
  debug() {
    if (!this.isDevelopment) return;
    
    console.group('App Configuration');
    console.log('Environment:', import.meta.env.MODE);
    console.log('Base URL:', this.baseUrl);
    console.log('API Base URL:', this.apiBaseUrl);
    console.log('Subdomain:', this.getSubdomain());
    console.log('App Context:', this.getAppContext());
    console.log('App Title:', this.getAppTitle());
    console.log('Auth0 Domain:', this.auth0Domain);
    console.log('Auth0 Redirect URI:', this.auth0RedirectUri);
    console.log('Auth0 Logout URI:', this.auth0LogoutUri);
    console.groupEnd();
  }
}

// Create and export singleton instance
const appConfig = new AppConfig();

// Validate configuration on load
if (!appConfig.validate()) {
  throw new Error('Invalid application configuration. Check your environment variables.');
}

export default appConfig;
